import {
    PropertyFieldBinding,
    type FireCMSPlugin,
    type PluginFormActionProps,
} from "@firecms/core";
import { cls, defaultBorderMixin } from "@firecms/ui";

/** The one collection this moves the switch for, and the field it moves. */
const COLLECTION_ID = "news";
const PROPERTY_KEY = "published";

/**
 * Renders `news.published` in the entity form's right-hand column, beside Save
 * and Discard, instead of in the form body.
 *
 * **A plugin because that column is not ours.** It is built by
 * `EntityEditViewFormActions` → `buildSideActions` inside `@firecms/core`, and
 * a collection definition cannot reach it — `properties` only ever populate the
 * form body. `form.Actions` is the single sanctioned slot in it.
 *
 * **`order-first` is what puts it above Save, and it is the only lever there
 * is.** That slot renders after the Save and Discard buttons, and the component
 * holding them is hardcoded in `EntityEditView` (`EntityFormActionsComponent={EntityEditViewFormActions}`)
 * — no collection or plugin option replaces it, and `form.ActionsTop` is a
 * different place entirely: the 56px top bar beside the view tabs, not this
 * column. Since the column is a `flex flex-col`, a flex `order` on our own
 * wrapper reorders it visually without touching FireCMS. The one cost is that
 * DOM order is unchanged, so Tab still reaches Save before the switch.
 *
 * The switch is bound through `PropertyFieldBinding` rather than rebuilt by
 * hand, so it stays the same field it was in the body: `StatusSwitchField`, the
 * localised property name, the description and the validation errors all come
 * from the collection's own `published` property. Nothing here knows what the
 * field looks like.
 *
 * `news` is the only collection with a side column to put this in — it is the
 * only `openEntityMode: "full_screen"` one — but plugin actions render on every
 * form, so the id check is what keeps it off the others.
 */
function PublishedSideAction({ collection, formContext }: PluginFormActionProps) {
    if (collection.id !== COLLECTION_ID) return null;
    if (!formContext) return null;

    const property = formContext.collection?.properties?.[PROPERTY_KEY];
    if (!property) return null;

    return (
        <div className={cls("order-first mb-4 pb-4 border-b", defaultBorderMixin)}>
            <PropertyFieldBinding
                propertyKey={PROPERTY_KEY}
                property={property}
                context={formContext}
                includeDescription={Boolean(property.description)}
                partOfArray={false}
                minimalistView={false}
                autoFocus={false}
                /**
                 * No `disabled` prop, deliberately. `PluginFormActionProps.disabled`
                 * is the *button* disabled flag — it includes
                 * `status === "existing" && !formex.dirty`, so passing it would
                 * freeze the switch on every pristine form and leave no way to
                 * publish an article without editing something else first.
                 * `PropertyFieldBinding` already applies `formContext.disabled`,
                 * which is the submitting/read-only state this field cares about.
                 */
            />
        </div>
    );
}

/**
 * Registered in `App.tsx` through `useBuildNavigationController`.
 *
 * Its only job is to host the component above; there is no other way to hand a
 * component to the form's side column.
 */
export const publishedSideSwitchPlugin: FireCMSPlugin = {
    key: "published_side_switch",
    form: {
        Actions: PublishedSideAction,
    },
};
